/**
 * Topbar — the horizontal chrome that sits above the routed content.
 *
 *   [ ☰ ]  ┌────────────── Search ──────────────┐              🔔  🌓  🙂
 *
 * Composition (left → right):
 *   • Hamburger — mobile only, opens the sidebar Sheet.
 *   • Global search — a button styled as an input; click or ⌘K/Ctrl+K
 *     opens the command palette (cmdk) inside a Radix Dialog.
 *   • Flexible spacer.
 *   • Theme toggle — dropdown with Light / Dark / System.
 *   • Notifications — bell with an unread badge, dropdown of recent items.
 *   • User avatar — dropdown with Profile / Settings / Log out.
 *
 * The command palette is scoped by the same nav data the sidebar uses so both
 * surfaces stay in lockstep.
 */
import {
  forwardRef,
  useEffect,
  useMemo,
  useState,
  type ElementRef,
  type HTMLAttributes,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import {
  Bell,
  Building2,
  Check,
  ChevronDown,
  Lock,
  LogOut,
  Menu,
  Monitor,
  Moon,
  Search,
  Settings,
  Sun,
  User as UserIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { NAV, filterNav } from '@/shared/nav';
import { useAuth } from '@/shared/auth/store';
import { useLogout } from '@/shared/auth/useLogin';
import {
  useAvailableCompanies,
  useCurrentCompany,
  type CurrentCompany,
} from '@/shared/auth/use-current-company';
import { resolveUploadUrl } from '@/shared/api/uploads';
import { relativeTime, useMarkAllNotificationsRead, useMarkNotificationRead, useNotifications } from '@/shared/features/notifications';
import { useHotkey } from '@/shared/hooks/use-hotkey';
import { TOPBAR_FOCUS_SEARCH_EVENT } from '@/shared/features/global-hotkeys';
import { useTheme } from './theme-provider';
import { Avatar, AvatarFallback, AvatarImage } from './avatar';
import { Badge } from './badge';
import { Button } from './button';
import { Dialog, DialogContent } from './dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { Kbd } from './kbd';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from './utils';

export interface TopbarProps extends HTMLAttributes<HTMLElement> {
  /** Called by the hamburger — opens the mobile sidebar drawer. */
  onOpenMobileNav?: () => void;
}

export const Topbar = forwardRef<ElementRef<'header'>, TopbarProps>(
  ({ onOpenMobileNav, className, ...rest }, ref) => {
    const user = useAuth((s) => s.user);
    const enabledModules = useAuth((s) => s.enabledModules);
    const roles = useAuth((s) => s.roles);
    const installAdmin = useAuth((s) => s.installAdmin);
    const logout = useLogout();
    const { theme, setTheme, resolvedTheme } = useTheme();
    const navigate = useNavigate();

    const [paletteOpen, setPaletteOpen] = useState(false);
    const [query, setQuery] = useState('');

    // ⌘K / Ctrl-K toggles the palette from anywhere.
    useHotkey({
      keys: 'ctrl+k',
      handler: () => setPaletteOpen((v) => !v),
      description: 'Open command palette',
    });

    // The `/` global-search-focus shortcut fires this event; opening the
    // palette is the click-equivalent of clicking the topbar search button.
    useEffect(() => {
      const onFocus = () => setPaletteOpen(true);
      window.addEventListener(TOPBAR_FOCUS_SEARCH_EVENT, onFocus);
      return () => window.removeEventListener(TOPBAR_FOCUS_SEARCH_EVENT, onFocus);
    }, []);

    // Flatten the nav for the palette, respecting module + role gating.
    const paletteItems = useMemo(() => {
      const sections = filterNav(NAV, enabledModules, roles, { installAdmin });
      return sections.flatMap((section) =>
        section.items.map((item) => ({ ...item, section: section.label ?? 'General' })),
      );
    }, [enabledModules, roles, installAdmin]);

    const notificationsQuery = useNotifications();
    const markRead = useMarkNotificationRead();
    const markAllRead = useMarkAllNotificationsRead();
    const notificationItems = notificationsQuery.data?.data ?? [];
    const unreadCount = notificationsQuery.data?.meta.unread_count ?? 0;
    const isMac = useMemo(
      () =>
        typeof navigator !== 'undefined' &&
        /Mac|iPhone|iPad|iPod/i.test(navigator.platform ?? ''),
      [],
    );

    const runCommand = (fn: () => void) => {
      setPaletteOpen(false);
      setQuery('');
      // Defer navigation until after the dialog close animation has committed
      // so focus restoration doesn't fight with the destination page's focus.
      queueMicrotask(fn);
    };

    return (
      <header
        ref={ref}
        {...rest}
        className={cn(
          'sticky top-0 z-20 flex h-12 shrink-0 items-center gap-3',
          'border-b border-border bg-card px-4',
          className,
        )}
      >
        {/* Hamburger — mobile only */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onOpenMobileNav}
          className="lg:hidden"
          aria-label="Open navigation menu"
        >
          <Menu />
        </Button>

        {/* Global search — button styled as an input; opens the palette. */}
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          aria-label={`Search — press ${isMac ? '⌘' : 'Ctrl'}+K`}
          className={cn(
            'group flex h-8 w-full max-w-[40rem] items-center gap-2 rounded-lg',
            'border border-border bg-card px-3 text-sm text-muted-foreground shadow-sm',
            'transition-colors hover:border-primary/40 hover:bg-accent/40',
            'focus-ring',
          )}
        >
          <Search className="size-4 shrink-0" aria-hidden />
          <span className="flex-1 truncate text-left">
            Search customers, orders, products…
          </span>
          <span className="hidden shrink-0 items-center gap-1 sm:inline-flex">
            <Kbd>{isMac ? '⌘' : 'Ctrl'}</Kbd>
            <Kbd>K</Kbd>
          </span>
        </button>

        <div className="flex-1" />

        {/* Company switcher — the topbar's "which company are we presenting"
            control. Per-session (session-cookie) so two demo browsers signed
            into the same login can hold DIFFERENT companies at the same time. */}
        <CompanySwitcher />

        {/* Theme toggle */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Change theme"
              title="Change theme"
            >
              {resolvedTheme === 'dark' ? <Moon /> : <Sun />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuItem onSelect={() => setTheme('light')}>
              <Sun />
              <span>Light</span>
              {theme === 'light' && <span className="ml-auto text-primary">•</span>}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setTheme('dark')}>
              <Moon />
              <span>Dark</span>
              {theme === 'dark' && <span className="ml-auto text-primary">•</span>}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setTheme('system')}>
              <Monitor />
              <span>System</span>
              {theme === 'system' && <span className="ml-auto text-primary">•</span>}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
              className="relative"
            >
              <Bell />
              {unreadCount > 0 && (
                <span
                  aria-hidden
                  className={cn(
                    'absolute right-1 top-1 grid h-4 min-w-4 place-items-center rounded-full',
                    'bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground',
                    'ring-2 ring-card',
                  )}
                >
                  {unreadCount}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 p-0">
            <div className="flex items-center justify-between px-3 py-2.5">
              <span className="text-sm font-semibold text-foreground">Notifications</span>
              {unreadCount > 0 && (
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={() => markAllRead.mutate()}
                >
                  Mark all read
                </button>
              )}
            </div>
            <DropdownMenuSeparator className="m-0" />
            <div className="max-h-80 overflow-y-auto py-1">
              {notificationItems.length === 0 && (
                <p className="px-3 py-6 text-center text-sm text-muted-foreground">You're all caught up.</p>
              )}
              {notificationItems.slice(0, 8).map((n) => (
                <DropdownMenuItem
                  key={n.id}
                  className="flex cursor-pointer flex-col items-start gap-0.5 rounded-none px-3 py-2"
                  onSelect={() => {
                    if (!n.read_at) markRead.mutate(n.id);
                    if (n.data.action_url) navigate(n.data.action_url);
                  }}
                >
                  <div className="flex w-full items-center gap-2">
                    {!n.read_at && (
                      <span
                        className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                        aria-hidden
                      />
                    )}
                    <span className="flex-1 truncate text-sm font-medium text-foreground">
                      {n.data.title}
                    </span>
                    <span className="text-xs text-muted-foreground">{relativeTime(n.created_at)}</span>
                  </div>
                  <span className="line-clamp-2 pl-3.5 text-xs text-muted-foreground">
                    {n.data.body}
                  </span>
                </DropdownMenuItem>
              ))}
            </div>
            <DropdownMenuSeparator className="m-0" />
            <DropdownMenuItem
              className="justify-center py-2 text-sm font-medium text-primary focus:text-primary"
              onSelect={() => navigate('/notifications')}
            >
              View all notifications
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Open account menu (top bar)"
              className="rounded-full focus-ring"
            >
              <Avatar className="h-7 w-7">
                {user?.avatar_path && (
                  <AvatarImage src={resolveUploadUrl(user.avatar_path)} alt={user?.name ?? ''} />
                )}
                <AvatarFallback name={user?.name ?? 'User'} />
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={8} className="w-60">
            <div className="flex flex-col gap-0.5 px-2 py-1.5">
              <span className="truncate text-sm font-medium text-foreground">
                {user?.name ?? 'Account'}
              </span>
              <span className="truncate text-xs text-muted-foreground">
                {user?.email ?? ''}
              </span>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate('/profile')}>
              <UserIcon />
              <span>Profile</span>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate('/admin/numbering')}>
              <Settings />
              <span>Settings</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => logout.mutate()}
              className="text-destructive focus:text-destructive"
            >
              <LogOut />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Command palette (⌘K) */}
        <Dialog open={paletteOpen} onOpenChange={setPaletteOpen}>
          <DialogContent className="max-w-xl overflow-hidden p-0 gap-0">
            <Command
              label="Global command palette"
              className="flex flex-col divide-y divide-border"
            >
              <div className="flex items-center gap-2 px-3">
                <Search
                  className="size-4 shrink-0 text-muted-foreground"
                  aria-hidden
                />
                <Command.Input
                  value={query}
                  onValueChange={setQuery}
                  placeholder="Search or jump to…"
                  className="flex h-11 w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
                />
                <Kbd className="hidden sm:inline-flex">ESC</Kbd>
              </div>

              <Command.List className="max-h-[24rem] overflow-y-auto p-2">
                <Command.Empty className="py-10 text-center text-sm text-muted-foreground">
                  No results found.
                </Command.Empty>

                <Command.Group
                  heading="Navigate"
                  className={cn(
                    '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2',
                    '[&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:font-bold',
                    '[&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider',
                    '[&_[cmdk-group-heading]]:text-muted-foreground',
                  )}
                >
                  {paletteItems.map((it) => {
                    const Icon = it.icon;
                    return (
                      <Command.Item
                        key={it.to}
                        value={`${it.section} ${it.label} ${it.to}`}
                        onSelect={() => runCommand(() => navigate(it.to))}
                        className={cn(
                          'flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-foreground',
                          'data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground',
                        )}
                      >
                        <Icon className="size-4 text-muted-foreground" aria-hidden />
                        <span className="flex-1 truncate">{it.label}</span>
                        <span className="ml-auto text-xs text-muted-foreground">
                          {it.section}
                        </span>
                      </Command.Item>
                    );
                  })}
                </Command.Group>
              </Command.List>

              <div className="flex items-center justify-between px-3 py-2 text-2xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Kbd>↑</Kbd>
                  <Kbd>↓</Kbd>
                  <span>to navigate</span>
                </span>
                <span className="flex items-center gap-1">
                  <Kbd>↵</Kbd>
                  <span>to select</span>
                </span>
                <span className="flex items-center gap-1">
                  <Kbd>Esc</Kbd>
                  <span>to close</span>
                </span>
              </div>
            </Command>
          </DialogContent>
        </Dialog>
      </header>
    );
  },
);
Topbar.displayName = 'Topbar';

/* ------------------------------------------------------------------ */
/*  Company switcher                                                  */
/* ------------------------------------------------------------------ */

/**
 * The topbar's Company Switcher.
 *
 * Trigger: an outlined button showing `<Building2/>` + the current company
 * name + an industry pill. Clicking opens a Radix Popover with a cmdk
 * Command inside so the operator can search and pick from every accessible
 * company, grouped by industry.
 *
 * Selecting a company POSTs to `/me/current-company`, invalidates every
 * TanStack Query cache (so every subsequently-rendered list refetches with
 * the new company_id scope), and closes the popover with a toast.
 *
 * The active company is persisted server-side on the SPA session cookie —
 * that's what makes two tabs on two browsers hold two different companies
 * simultaneously.
 */
function CompanySwitcher() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { company, isLoading, switchCompany, isSwitching } = useCurrentCompany();
  const { data: available = [], isLoading: loadingAvailable } = useAvailableCompanies();

  // Group by industry_label so the picker shows an "Other" bucket for
  // non-demo companies alongside the seeded industry groups.
  const grouped = useMemo(() => {
    const map = new Map<string, CurrentCompany[]>();
    for (const c of available) {
      const label = c.industry_label ?? 'Other';
      const list = map.get(label);
      if (list) list.push(c);
      else map.set(label, [c]);
    }
    // Stable alphabetical order, "Other" last.
    return Array.from(map.entries()).sort((a, b) => {
      if (a[0] === 'Other') return 1;
      if (b[0] === 'Other') return -1;
      return a[0].localeCompare(b[0]);
    });
  }, [available]);

  const onPick = async (c: CurrentCompany) => {
    if (company?.id === c.id) {
      setOpen(false);
      return;
    }
    try {
      await switchCompany(c.id);
      toast.success(`Switched to ${c.name}`);
      setOpen(false);
      setQuery('');
    } catch {
      /* toast is fired inside the mutation's onError. */
    }
  };

  const triggerLabel = company?.name ?? (isLoading ? 'Loading…' : 'Pick a company');

  // Nothing to switch to: the session was opened from a per-industry sign-in
  // link (the server refuses switching), or this person only has one company.
  // A dropdown that opens onto one entry — or onto a switch that is refused —
  // is a control that cannot do anything, so show the company as a label.
  const pinned = !!company?.locked;
  if (company && (pinned || (!loadingAvailable && available.length <= 1))) {
    return (
      <div
        className="flex h-8 max-w-[300px] items-center gap-2 rounded-md border border-border bg-card px-2.5 text-sm"
        title={
          pinned
            ? `${company.name} — this session is limited to this company. Sign out to open another.`
            : company.name
        }
      >
        <Building2 className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="max-w-[200px] truncate font-medium text-foreground">{company.name}</span>
        {company.industry_label ? (
          <Badge variant="neutral" size="sm" className="shrink-0">
            {company.industry_label}
          </Badge>
        ) : null}
        {pinned ? (
          <Lock className="size-3.5 shrink-0 text-muted-foreground" aria-label="Limited to this company" />
        ) : null}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 max-w-[280px] gap-2 px-2.5"
          aria-label="Switch company"
          title={company?.name ?? 'Pick a company'}
          loading={isSwitching}
          leftIcon={<Building2 />}
          rightIcon={<ChevronDown className="opacity-60" />}
        >
          <span className="max-w-[220px] truncate text-left">{triggerLabel}</span>
          {company?.industry_label && (
            <Badge variant="neutral" size="sm" className="ml-1 shrink-0">
              {company.industry_label}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-[320px] p-0">
        <Command
          label="Switch company"
          className="flex flex-col divide-y divide-border"
          // Widen cmdk's default fuzzy filter so a search on "pharma"
          // matches both a company named "Aashirwaad Pharma" and one
          // tagged with the `pharma` industry key.
          filter={(value, search) => {
            if (!search) return 1;
            return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <div className="flex items-center gap-2 px-3">
            <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            <Command.Input
              value={query}
              onValueChange={setQuery}
              placeholder="Search company…"
              className="flex h-10 w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
            />
          </div>
          <Command.List className="max-h-[22rem] overflow-y-auto p-1.5">
            {loadingAvailable ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                Loading companies…
              </div>
            ) : available.length === 0 ? (
              <Command.Empty className="px-3 py-6 text-center text-xs text-muted-foreground">
                No companies available yet.
              </Command.Empty>
            ) : (
              <>
                <Command.Empty className="px-3 py-6 text-center text-xs text-muted-foreground">
                  No matches.
                </Command.Empty>
                {grouped.map(([label, items]) => (
                  <Command.Group
                    key={label}
                    heading={label}
                    className={cn(
                      '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2',
                      '[&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:font-bold',
                      '[&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider',
                      '[&_[cmdk-group-heading]]:text-muted-foreground',
                    )}
                  >
                    {items.map((c) => {
                      const active = company?.id === c.id;
                      return (
                        <Command.Item
                          key={c.id}
                          value={`${c.name} ${c.code} ${c.industry_key ?? ''} ${c.industry_label ?? ''}`}
                          onSelect={() => onPick(c)}
                          className={cn(
                            'flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground',
                            'data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground',
                          )}
                        >
                          <Building2
                            className="size-4 shrink-0 text-muted-foreground"
                            aria-hidden
                          />
                          <div className="min-w-0 flex-1">
                            <div className="truncate">{c.name}</div>
                            <div className="truncate text-2xs text-muted-foreground">
                              {c.code}
                            </div>
                          </div>
                          {c.industry_label && (
                            <Badge variant="neutral" size="sm" className="ml-1 shrink-0">
                              {c.industry_label}
                            </Badge>
                          )}
                          {active && (
                            <Check className="ml-1 size-4 shrink-0 text-primary" aria-hidden />
                          )}
                        </Command.Item>
                      );
                    })}
                  </Command.Group>
                ))}
              </>
            )}
          </Command.List>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
